/**
 * Lista de opciones cerrada (`choices` de Django).
 *
 * Un `<select>` nativo y no un combobox propio: es la unica forma de que en un
 * celular se abra la rueda de opciones del sistema operativo, que es mucho mas
 * usable que cualquier desplegable dibujado en HTML.
 */

import { controlClass } from '@/components/fields/controls';
import { ReadonlyOutput } from '@/components/fields/FieldDisplay';
import type { FieldInputProps } from '@/components/fields/types';
import type { JsonPrimitive } from '@/lib/types';

/** Las opciones viajan como string en el DOM; hay que devolverlas a su tipo. */
function devolverTipo(texto: string, opciones: Array<{ value: JsonPrimitive }>): JsonPrimitive {
  const encontrada = opciones.find((opcion) => String(opcion.value) === texto);
  return encontrada === undefined ? texto : encontrada.value;
}

export function SelectionField({
  id,
  meta,
  value,
  onChange,
  readonly,
  required,
  invalid,
  describedBy,
}: FieldInputProps) {
  if (readonly) {
    return <ReadonlyOutput id={id} meta={meta} value={value} {...(describedBy === undefined ? {} : { describedBy })} />;
  }

  const opciones = meta.choices ?? [];

  return (
    <select
      id={id}
      className={controlClass(invalid, 'pr-8')}
      value={value === null || value === undefined ? '' : String(value)}
      required={required}
      aria-required={required || undefined}
      aria-invalid={invalid || undefined}
      aria-describedby={describedBy}
      onChange={(evento) => {
        const texto = evento.target.value;
        onChange(texto === '' ? null : devolverTipo(texto, opciones));
      }}
    >
      {/* Sin opcion vacia si el campo es obligatorio: no tiene sentido ofrecer
          un estado que la validacion va a rechazar. */}
      {(!required || value === null || value === undefined) && <option value="">Sin definir</option>}
      {opciones.map((opcion) => (
        <option key={String(opcion.value)} value={String(opcion.value)}>
          {opcion.label}
        </option>
      ))}
    </select>
  );
}
